/**
 * The host dashboard's pure bits — round-3 handoff §2 (docs/plans/2026-09-header-host-pros).
 * Buckets, the status pill, the shot clock and the bars, kept out of the components so the
 * rules are testable without rendering.
 *
 * Bucket → backend status (Event.cs): draft → 'draft'; live → 'selling' (Guaranteed) |
 * 'funding' | 'processing_greenlight' | 'greenlit' (Party Starter); ended → 'ended' |
 * 'canceled' | 'dissolved'. Past-date selling/greenlit events arrive already rewritten to
 * 'ended' by the DTO's EffectiveStatus.
 */
export const LIVE_STATUSES = ['selling', 'funding', 'processing_greenlight', 'greenlit'];
export const ENDED_STATUSES = ['ended', 'canceled', 'dissolved'];
export const FILTERS = [['draft', 'DRAFT'], ['live', 'LIVE'], ['ended', 'ENDED']];

export function bucketOf(status) {
  if (status === 'draft') return 'draft';
  if (LIVE_STATUSES.includes(status)) return 'live';
  if (ENDED_STATUSES.includes(status)) return 'ended';
  return null;
}

/** [label, background, colour] — handoff §2c. */
export const STATUS_PILL = {
  funding: ['FUNDING', '#FF00FF', '#fff'],
  selling: ['SELLING', '#22C55E', '#fff'],
  processing_greenlight: ['GREENLIT', '#00FFFF', '#000'],
  greenlit: ['GREENLIT', '#00FFFF', '#000'],
  draft: ['DRAFT', '#fff', '#000'],
  ended: ['ENDED', '#000', '#fff'],
  canceled: ['ENDED', '#000', '#fff'],
  dissolved: ['DISSOLVED', '#F87171', '#fff'],
};
export const statusPill = (status) => STATUS_PILL[status] || STATUS_PILL.draft;

const daysUntil = (iso, now) => Math.ceil((new Date(iso) - now) / 86400000);
const dayTag = (n) => (n < 0 ? 'PAST' : n === 0 ? 'TODAY' : n === 1 ? '1 DAY' : `${n} DAYS`);

/**
 * The tag top-right of the card and its sub-label: `4 DAYS · TO FUND` while a Party Starter
 * is funding, `17 DAYS · TO PARTY` for anything else that is live, `—` for a draft, `DONE`
 * once it is over. `tone` picks the background: magenta funding, black live, grey otherwise.
 */
export function shotClock(event, now = new Date()) {
  const bucket = bucketOf(event.status);
  if (bucket === 'draft' || !bucket) return { tag: '—', label: 'NOT PUBLISHED', tone: 'muted' };
  if (bucket === 'ended') {
    return { tag: event.status === 'ended' ? 'DONE' : event.status.toUpperCase(), label: event.status === 'ended' ? 'WRAPPED' : 'NO EVENT', tone: 'muted' };
  }
  if (event.status === 'funding' && event.fundingDeadline) {
    return { tag: dayTag(daysUntil(event.fundingDeadline, now)), label: 'TO FUND', tone: 'funding' };
  }
  return { tag: event.dateTime ? dayTag(daysUntil(event.dateTime, now)) : '—', label: 'TO EVENT', tone: 'live' };
}

/** sold / cap and the fund's cents / goal, each as a 0–100 percentage for the bars. */
export function ticketProgress(event) {
  const sold = event.acceptedCount || 0;
  const cap = event.maxAttendees || 0;
  return { sold, cap, pct: cap > 0 ? Math.min(100, Math.round((sold / cap) * 100)) : 0 };
}

export function fundProgress(event) {
  const cents = event.totalPledgedCents || 0;
  const goal = event.fundingGoalCents || 0;
  return { cents, goal, pct: goal > 0 ? Math.min(100, Math.round((cents / goal) * 100)) : 0 };
}

export function goingLabel(count) {
  return count > 0 ? `${count} GOING` : 'NO ONE YET';
}

/** MANAGE → for the live, FINISH SETUP → for a draft, RECAP → once it is over. */
export function manageLabel(bucket) {
  return bucket === 'draft' ? 'FINISH SETUP →' : bucket === 'ended' ? 'RECAP →' : 'MANAGE →';
}

export function emptyLabel(bucket) {
  return bucket === 'draft' ? 'NO DRAFTS.' : bucket === 'ended' ? 'NOTHING ENDED YET.' : 'NOTHING LIVE.';
}

/** `CITY · VENUE`, whichever parts exist, upper-cased. */
export function placeLine(event) {
  return [event.locationCity, event.venueName].filter(Boolean).map((s) => s.toUpperCase()).join(' · ');
}

/**
 * Where a row on the dashboard goes. Signals are edited through their own composer, never the
 * generic event manage/edit flow: Portal Noir signals go back into Portal Noir; regular ones
 * into the signal composer. Drafts go to the builder.
 */
export function eventPath(event) {
  if (event?.isPortalSignal) {
    // MANAGE opens the Portal's own page (who wants in, edit, invite, delete), not straight
    // into the editor. Portal Noir's signals keep their own pages.
    return event.signalScope === 'portal' ? `/noir/signals/${event.id}/edit` : `/portals/${event.id}`;
  }
  if (event.status === 'draft') return `/host/events/${event.id}/edit`;
  return `/host/events/${event.id}`;
}

/** The seven segments of a plan's progress bar: done ones black, the rest #E5E5E5. */
export function planSegments(plan) {
  const total = plan.stageCount || 7;
  const done = Math.max(0, Math.min(total, plan.stagesDone || 0));
  return Array.from({ length: total }, (_, i) => i < done);
}
